import { apiClient } from './client';

/**
 * Offline conversion upload API client (Data Manager API → Enhanced
 * Conversions for Leads).
 *
 * Mirrors the backend at `src/api/routers/offline_conversions.py`. Every
 * endpoint is gated behind `require_offline_conversions_access` server-side;
 * this client just calls them and surfaces 403/409 like any other error.
 *
 * The shared types live here so the wizard's Zod schemas and the API client
 * have a single source of truth (Theme #3 — frontend/backend name drift).
 */

// ---------------------------------------------------------------------------
// Types — must match src/api/models/offline_conversion.py exactly
// ---------------------------------------------------------------------------

export type ConsentStatus =
  | 'CONSENT_STATUS_UNSPECIFIED'
  | 'CONSENT_GRANTED'
  | 'CONSENT_DENIED';

export interface Consent {
  ad_user_data: ConsentStatus;
  ad_personalization: ConsentStatus;
}

export interface HashedAddress {
  hashed_first_name: string;
  hashed_last_name: string;
  country_code: string;
  postal_code: string;
}

export interface OfflineConversionEvent {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  /** RFC 3339 UTC timestamp. */
  event_timestamp: string;
  conversion_value?: number;
  currency?: string;
  /** Google's idempotency key — stable per-lead transaction ID. */
  transaction_id?: string;
  hashed_email?: string;
  hashed_phone?: string;
  hashed_address?: HashedAddress;
  consent?: Consent;
}

export interface UploadEnhancedLeadsRequest {
  google_ads_account_id: string;
  product_destination_id: string;
  encoding?: 'HEX' | 'BASE64';
  validate_only?: boolean;
  consent?: Consent;
  events: OfflineConversionEvent[];
}

export interface FlouriRowRejection {
  row_index: number;
  reason: string;
  field?: string;
}

export interface FlouriValidationReport {
  total_rows: number;
  accepted_rows: number;
  rejections: FlouriRowRejection[];
}

export interface UploadEnhancedLeadsResponse {
  success: boolean;
  /** Null for validate_only requests. */
  request_id: string | null;
  flouri_validation: FlouriValidationReport;
}

export interface WarningCount {
  reason: string;
  record_count: number;
}
export interface ErrorCount {
  reason: string;
  record_count: number;
}

/**
 * Per-destination status from RetrieveRequestStatus. Critically does NOT
 * carry `upload_match_rate_range` — that field belongs to audience-member
 * ingestion statuses (Customer Match), not events_ingestion_status. For
 * attributed counts, point users at Google Ads conversion reporting.
 */
export interface EventsIngestionStatus {
  status: 'PROCESSING' | 'SUCCESS' | 'FAILURE' | 'PARTIAL_SUCCESS';
  record_count: number;
  warning_info: WarningCount[];
  error_info: ErrorCount[];
}

export interface RequestStatusRequest {
  google_ads_account_id: string;
  request_id: string;
}

export interface RequestStatusResponse {
  request_id: string;
  request_status_per_destination: EventsIngestionStatus[];
}

export interface ECLPreflightResult {
  customer_data_terms_accepted: boolean;
  enhanced_conversions_for_leads_enabled: boolean;
  /** When centralized at the MCC, this is the manager account ID. */
  conversion_tracking_customer_id: string | null;
}

export interface ConversionActionSummary {
  id: string;
  name: string;
  category: string | null;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

const BASE = '/offline-conversions';

export const offlineConversionsApi = {
  /**
   * Return whether the current user can access the offline conversion upload
   * flow. Backend env-var-driven allowlist; no frontend mirror.
   */
  async access(): Promise<boolean> {
    const res = await apiClient.get<{ has_access: boolean }>(`${BASE}/access`);
    return res.data.has_access;
  },

  /**
   * Preflight: confirm Customer Data Terms accepted + ECL turned on.
   * Call before showing the upload form to surface actionable errors up
   * front rather than after a full CSV.
   */
  async preflight(googleAdsAccountId: string): Promise<ECLPreflightResult> {
    const res = await apiClient.get<ECLPreflightResult>(`${BASE}/preflight`, {
      params: { google_ads_account_id: googleAdsAccountId },
    });
    return res.data;
  },

  /**
   * List Google Ads conversion actions of type=UPLOAD_CLICKS — the only
   * valid destinations for offline ECL uploads.
   */
  async listEligibleActions(googleAdsAccountId: string): Promise<ConversionActionSummary[]> {
    const res = await apiClient.get<ConversionActionSummary[]>(`${BASE}/upload-eligible-actions`, {
      params: { google_ads_account_id: googleAdsAccountId },
    });
    return res.data;
  },

  /**
   * Validate-only ingest. Google checks request shape but does not return
   * per-event acceptance and does not produce diagnostics. Use this as a
   * pre-flight on the actual API call, not as a row-level validator (our
   * client-side check does that).
   */
  async validate(req: UploadEnhancedLeadsRequest): Promise<UploadEnhancedLeadsResponse> {
    const res = await apiClient.post<UploadEnhancedLeadsResponse>(`${BASE}/validate`, req);
    return res.data;
  },

  /**
   * Real ingest. Response carries a request_id; per-destination outcomes
   * arrive asynchronously via `status()`.
   */
  async upload(req: UploadEnhancedLeadsRequest): Promise<UploadEnhancedLeadsResponse> {
    const res = await apiClient.post<UploadEnhancedLeadsResponse>(`${BASE}/upload`, req);
    return res.data;
  },

  /**
   * Diagnostics poll. Google's guidance: wait 30 min before first call,
   * exponentially back off (1.3x) up to 60-min intervals, give up at 24 h.
   * Backend rejects request_ids we didn't create for this member.
   */
  async status(req: RequestStatusRequest): Promise<RequestStatusResponse> {
    const res = await apiClient.post<RequestStatusResponse>(`${BASE}/status`, req);
    return res.data;
  },
};
